import { Checkbox, Label } from "flowbite-react";
import { ExternalLink } from "lucide-react";
import { YEATIES_CONSENT_CHECKBOX_LABEL } from "@/features/card/configs/yeatiesConsent.config";
import { YEATIES_PRIVACY_LINK_LABEL, YEATIES_PRIVACY_PATH } from "@/features/legal/config/yeatiesPrivacy.config";
import { YEATIES_TERMS_LINK_LABEL, YEATIES_TERMS_PATH } from "@/features/legal/config/yeatiesTerms.config";
import { YEATIES_CONSENT_NEW_TAB_HINT } from "@/features/purchasePoints/config/purchasePoints.config";
import { cn } from "@/shared/utils/style.util";

const CHECKBOX_ID = "yeaties-consent-checkbox";
const EXTERNAL_LINK_ICON_SIZE = 16;

const BOX_CLASS = "w-full max-w-xl rounded-xl border-2 px-4 py-3 transition-colors";
const BOX_CHECKED_CLASS = "border-primary bg-lightprimary dark:bg-darkprimary";
const BOX_UNCHECKED_CLASS = "border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800";
// The label spans the row, so the tap target stays at 44px although the box itself is 20px.
const LABEL_CLASS = "min-h-11 flex-1 py-2.5 text-base leading-6 text-gray-900 dark:text-white";
const CHECKBOX_CLASS = "mt-3 size-5 shrink-0 focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800";
const LINK_CLASS = "inline-flex items-center gap-1 text-sm text-darkprimary underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-white";

type YeatiesConsentLinkProps = { href: string, label: string, testId: string };

const YeatiesConsentLink = ({ href, label, testId }: YeatiesConsentLinkProps) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS} data-testid={testId}>
    {label}
    <ExternalLink size={EXTERNAL_LINK_ICON_SIZE} aria-hidden="true" />
    <span className="sr-only">{YEATIES_CONSENT_NEW_TAB_HINT}</span>
  </a>
);

type YeatiesConsentCheckboxProps = {
  isChecked: boolean
  onChange: (isChecked: boolean) => void
};

export const YeatiesConsentCheckbox = ({ isChecked, onChange }: YeatiesConsentCheckboxProps) => (
  <div className={cn(BOX_CLASS, isChecked ? BOX_CHECKED_CLASS : BOX_UNCHECKED_CLASS)} data-testid="yeaties-consent-box">
    <div className="flex items-start gap-3">
      <Checkbox
        id={CHECKBOX_ID}
        className={CHECKBOX_CLASS}
        checked={isChecked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <Label htmlFor={CHECKBOX_ID} className={LABEL_CLASS}>
        {YEATIES_CONSENT_CHECKBOX_LABEL}
      </Label>
    </div>
    {/* Outside the label: a click on a link must open the document, not tick the box. */}
    <div className="flex flex-col pl-8 gap-1">
      <YeatiesConsentLink href={YEATIES_TERMS_PATH} label={YEATIES_TERMS_LINK_LABEL} testId="yeaties-terms-link" />
      <YeatiesConsentLink href={YEATIES_PRIVACY_PATH} label={YEATIES_PRIVACY_LINK_LABEL} testId="yeaties-privacy-link" />
    </div>
  </div>
);

YeatiesConsentCheckbox.displayName = "YeatiesConsentCheckbox";
